import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaFileContract, FaInfoCircle, FaGavel } from 'react-icons/fa';
import './LegalPages.scss';

const TermsConditions: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Terms & Conditions - App Developer Pro';
  }, []);

  const lastUpdated = 'October 24, 2025';

  return (
    <div className="legal-page">
      {/* Hero Section */}
      <section className="legal-page__hero">
        <div className="legal-page__hero-bg"></div>
        <div className="legal-page__container">
          <motion.div
            className="legal-page__hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="legal-page__icon-wrapper">
              <FaFileContract className="legal-page__hero-icon" />
            </div>
            <h1 className="legal-page__title">Terms & Conditions</h1>
            <p className="legal-page__subtitle">
              Please read these terms and conditions carefully before using our services
            </p>
            <div className="legal-page__meta">
              <span className="legal-page__meta-item">
                <FaInfoCircle /> Last Updated: {lastUpdated}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="legal-page__content">
        <div className="legal-page__container">
          <motion.div
            className="legal-page__content-wrapper"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Introduction */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">
                <FaGavel /> Agreement to Terms
              </h2>
              <p className="legal-page__text">
                These Terms and Conditions constitute a legally binding agreement made between you, whether personally 
                or on behalf of an entity ("you") and App Developer Pro ("Company", "we", "us", or "our"), concerning 
                your access to and use of the appdeveloperpro.com website as well as any other media form, media channel, 
                mobile website or mobile application related, linked, or otherwise connected thereto (collectively, the "Site").
              </p>
              <div className="legal-page__highlight-box">
                <FaInfoCircle className="legal-page__highlight-icon" />
                <p>
                  <strong>By accessing this website, you agree to be bound by these Terms and Conditions.</strong> 
                  If you disagree with any part of these terms, you must not use our website.
                </p>
              </div>
            </div>

            {/* Intellectual Property */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">Intellectual Property Rights</h2>
              <p className="legal-page__text">
                Unless otherwise stated, App Developer Pro and/or its licensors own the intellectual property rights 
                for all material on this website. All intellectual property rights are reserved. You may access this 
                from appdeveloperpro.com for your own personal use subjected to restrictions set in these terms and conditions.
              </p>
              <p className="legal-page__text">
                <strong>You must not:</strong>
              </p>
              <div className="legal-page__list">
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Republish material from this website without proper attribution</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Sell, rent or sub-license material from the website</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Reproduce, duplicate or copy material from the website for commercial purposes</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Redistribute content from App Developer Pro (unless content is specifically made for redistribution)</span>
                </div>
              </div>
            </div>

            {/* User Accounts */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">User Accounts</h2>
              <p className="legal-page__text">
                When you create an account with us, you must provide us information that is accurate, complete, and 
                current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate 
                termination of your account on our Service.
              </p>
              <p className="legal-page__text">
                You are responsible for safeguarding the password that you use to access the Service and for any 
                activities or actions under your password, whether your password is with our Service or a third-party service.
              </p>
              <p className="legal-page__text">
                You agree not to disclose your password to any third party. You must notify us immediately upon 
                becoming aware of any breach of security or unauthorized use of your account.
              </p>
            </div>

            {/* Services */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">Services and Deliverables</h2>
              <p className="legal-page__text">
                App Developer Pro provides web development, mobile app development, and related digital services. 
                The specific scope of work will be outlined in a separate Statement of Work (SOW) or project agreement.
              </p>
              <div className="legal-page__list">
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span><strong>Project Timelines:</strong> We strive to meet agreed-upon deadlines, but timelines may be adjusted based on project complexity</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span><strong>Revisions:</strong> A specified number of revisions will be included in the project scope</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span><strong>Client Responsibilities:</strong> Clients must provide timely feedback and necessary materials</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span><strong>Additional Work:</strong> Changes beyond the original scope will be billed separately</span>
                </div>
              </div>
            </div>

            {/* Payment Terms */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">Payment Terms</h2>
              <p className="legal-page__text">
                Payment terms will be specified in your project agreement. Generally:
              </p>
              <div className="legal-page__list">
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>A deposit (typically 50%) is required before work begins</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Final payment is due upon project completion</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Late payments may incur interest charges</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>We reserve the right to withhold deliverables until full payment is received</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Refunds are subject to our refund policy outlined in the project agreement</span>
                </div>
              </div>
            </div>

            {/* Acceptable Use */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">Acceptable Use</h2>
              <p className="legal-page__text">
                You may not use our website or services for any illegal or unauthorized purpose. You must not, 
                in the use of the Service, violate any laws in your jurisdiction.
              </p>
              <p className="legal-page__text">
                <strong>Prohibited activities include but are not limited to:</strong>
              </p>
              <div className="legal-page__list">
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Systematic retrieval of data or content to create a collection, compilation, database or directory</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Unauthorized use of automated systems or software to extract data from the website</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Attempting to circumvent any security measures</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Harassment, abuse, or harm of another person</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Uploading or transmitting viruses or any other type of malicious code</span>
                </div>
              </div>
            </div>

            {/* Confidentiality */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">Confidentiality</h2>
              <p className="legal-page__text">
                Both parties agree to maintain confidentiality of any proprietary information shared during the 
                course of the project. This includes but is not limited to:
              </p>
              <div className="legal-page__list">
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Business strategies and plans</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Technical information and trade secrets</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Customer and financial data</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Any information marked as confidential</span>
                </div>
              </div>
            </div>

            {/* Warranties */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">Warranties and Disclaimers</h2>
              <p className="legal-page__text">
                We warrant that services will be performed with reasonable skill and care in accordance with industry 
                standards. However:
              </p>
              <div className="legal-page__list">
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>We do not guarantee that the Service will be uninterrupted or error-free</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>We are not responsible for third-party services or content</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Warranty period for delivered work is typically 30-90 days from project completion</span>
                </div>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">Limitation of Liability</h2>
              <p className="legal-page__text">
                In no event shall App Developer Pro, nor its directors, employees, partners, agents, suppliers, 
                or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, 
                including without limitation, loss of profits, data, use, goodwill, or other intangible losses, 
                resulting from:
              </p>
              <div className="legal-page__list">
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Your access to or use of or inability to access or use the Service</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Any conduct or content of any third party on the Service</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Any content obtained from the Service</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Unauthorized access, use or alteration of your transmissions or content</span>
                </div>
              </div>
            </div>

            {/* Indemnification */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">Indemnification</h2>
              <p className="legal-page__text">
                You agree to defend, indemnify and hold harmless App Developer Pro and its licensee and licensors, 
                and their employees, contractors, agents, officers and directors, from and against any and all claims, 
                damages, obligations, losses, liabilities, costs or debt, and expenses arising from:
              </p>
              <div className="legal-page__list">
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Your use and access of the Service</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Your violation of any term of these Terms</span>
                </div>
                <div className="legal-page__list-item">
                  <span className="legal-page__bullet">•</span>
                  <span>Your violation of any third party right</span>
                </div>
              </div>
            </div>

            {/* Termination */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">Termination</h2>
              <p className="legal-page__text">
                We may terminate or suspend access to our Service immediately, without prior notice or liability, 
                for any reason whatsoever, including without limitation if you breach the Terms.
              </p>
              <p className="legal-page__text">
                Upon termination, your right to use the Service will immediately cease. If you wish to terminate 
                your account, you may simply discontinue using the Service.
              </p>
              <p className="legal-page__text">
                All provisions of the Terms which by their nature should survive termination shall survive termination, 
                including, without limitation, ownership provisions, warranty disclaimers, indemnity and limitations of liability.
              </p>
            </div>

            {/* Governing Law */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">Governing Law</h2>
              <p className="legal-page__text">
                These Terms shall be governed and construed in accordance with the laws of the jurisdiction in which 
                App Developer Pro operates, without regard to its conflict of law provisions.
              </p>
              <p className="legal-page__text">
                Our failure to enforce any right or provision of these Terms will not be considered a waiver of those 
                rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining 
                provisions of these Terms will remain in effect.
              </p>
            </div>

            {/* Changes to Terms */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">Changes to Terms</h2>
              <p className="legal-page__text">
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a 
                revision is material we will try to provide at least 30 days notice prior to any new terms taking effect.
              </p>
              <p className="legal-page__text">
                What constitutes a material change will be determined at our sole discretion. By continuing to access 
                or use our Service after those revisions become effective, you agree to be bound by the revised terms.
              </p>
            </div>

            {/* Contact */}
            <div className="legal-page__section">
              <h2 className="legal-page__heading">Contact Us</h2>
              <p className="legal-page__text">
                If you have any questions about these Terms and Conditions, please contact us:
              </p>
              <div className="legal-page__contact-box">
                <p><strong>Email:</strong> hello@appdeveloperpro.com</p>
                <p><strong>Phone:</strong> (882) 636-0188</p>
                <p><strong>Address:</strong> 827, Kachpura, Kundol, Agra, Uttar Pradesh 283111</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;

